import React, { useState } from 'react';
import { Wallet, ChevronDown, ChevronUp, Copy, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useWallet, NETWORK_CONFIG, getMetaMaskDeepLink, isMobileBrowser } from '@/hooks/useWallet';
import { HEP_TOKEN_ADDRESS } from '@/contracts/HepToken';

const MetaMaskSetupCard: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { addNetworkToWallet, addHepTokenToWallet } = useWallet();
  const hasMetaMask = typeof window !== 'undefined' && !!window.ethereum;
  const isMobile = isMobileBrowser();

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied!`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const networkRows = [
    { label: 'Network Name', value: NETWORK_CONFIG.name },
    { label: 'RPC URL', value: NETWORK_CONFIG.rpcUrl },
    { label: 'Chain ID', value: String(NETWORK_CONFIG.chainId) },
    { label: 'Currency Symbol', value: NETWORK_CONFIG.symbol },
  ];

  const tokenRows = [
    { label: 'Contract Address', value: HEP_TOKEN_ADDRESS },
    { label: 'Symbol', value: 'HEP' },
    { label: 'Decimals', value: '2' },
  ];

  return (
    <div className="bg-[#2a2a2a] border border-[#444] rounded-xl overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={() => setIsExpanded(prev => !prev)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#333] transition-colors"
      >
        <div className="flex items-center gap-2 text-gray-300">
          <Wallet className="w-4 h-4 text-[#F4D03F]" />
          <span className="text-sm font-medium">Set up MetaMask</span>
        </div>
        {isExpanded
          ? <ChevronUp className="w-4 h-4 text-gray-400" />
          : <ChevronDown className="w-4 h-4 text-gray-400" />
        }
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-5 pb-5 space-y-5 border-t border-[#444]">

          {/* Mobile without MetaMask — single deep-link CTA */}
          {!hasMetaMask && isMobile && (
            <div className="pt-4">
              <a
                href={getMetaMaskDeepLink()}
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-[#F4D03F] text-black text-sm font-semibold hover:bg-[#F7DC6F] transition-colors"
              >
                <Wallet className="w-4 h-4" />
                Open in MetaMask
              </a>
            </div>
          )}

          {/* Section A — Add Network */}
          <div className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Network</h3>
              {hasMetaMask && (
                <button
                  onClick={() => addNetworkToWallet()}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#F4D03F] text-black text-xs font-semibold hover:bg-[#F7DC6F] transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Add {NETWORK_CONFIG.name} Network
                </button>
              )}
            </div>
            <div className="rounded-lg border border-[#555] overflow-hidden">
              {networkRows.map((row, i) => (
                <div
                  key={row.label}
                  className={`flex items-center justify-between px-3 py-2 ${i !== networkRows.length - 1 ? 'border-b border-[#555]' : ''}`}
                >
                  <span className="text-xs text-gray-400 w-32 shrink-0">{row.label}</span>
                  <span className="text-xs text-gray-200 font-mono truncate flex-1 mx-2">{row.value}</span>
                  <button
                    onClick={() => copyToClipboard(row.value, row.label)}
                    className="shrink-0 text-gray-500 hover:text-gray-300 transition-colors"
                    title={`Copy ${row.label}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Section B — Add HEP Token */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">HEP Token</h3>
              {hasMetaMask && (
                <button
                  onClick={() => addHepTokenToWallet()}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#3a3a3a] border border-[#555] text-gray-200 text-xs font-semibold hover:bg-[#444] transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  Add HEP Token
                </button>
              )}
            </div>
            <div className="rounded-lg border border-[#555] overflow-hidden">
              {tokenRows.map((row, i) => (
                <div
                  key={row.label}
                  className={`flex items-center justify-between px-3 py-2 ${i !== tokenRows.length - 1 ? 'border-b border-[#555]' : ''}`}
                >
                  <span className="text-xs text-gray-400 w-32 shrink-0">{row.label}</span>
                  <span className="text-xs text-gray-200 font-mono truncate flex-1 mx-2">{row.value}</span>
                  <button
                    onClick={() => copyToClipboard(row.value, row.label)}
                    className="shrink-0 text-gray-500 hover:text-gray-300 transition-colors"
                    title={`Copy ${row.label}`}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetaMaskSetupCard;
